import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Reuse same instance settings as App (same-origin /api in production)

const API_BASE =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? "/api"
    : "http://localhost:8000");

const axiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

function Leaderboards() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedProfiles, setExpandedProfiles] = useState(new Set());
  const [stats, setStats] = useState({ total: 0, banned: 0 });

  useEffect(() => {
    loadGlobalProfiles();
  }, []);

  async function loadGlobalProfiles() {
    try {
      const res = await axiosInstance.get('/profiles/global');
      setProfiles(res.data.profiles || []);
      setStats({
        total: res.data.total_count || 0,
        banned: res.data.banned_count || 0
      });
    } catch (e) {
      console.error("Failed to load global profiles", e);
      setError("Failed to load leaderboards data");
    } finally {
      setLoading(false);
    }
  }

  const toggleProfileExpansion = (steamId) => {
    setExpandedProfiles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(steamId)) {
        newSet.delete(steamId);
      } else {
        newSet.add(steamId);
      }
      return newSet;
    });
  };

  const ProfileCard = ({ profile }) => {
    const isExpanded = expandedProfiles.has(profile.steam_id);
    
    const getStatusColor = (status) => {
      switch (status) {
        case "Banned": return "#dc3545";
        case "Suspicious": return "#ffc107";
        case "Clean": return "#28a745";
        default: return "#6c757d";
      }
    };

    const getBanInfo = () => {
      if (profile.vac_banned && profile.game_banned) {
        return "VAC + Game Banned";
      } else if (profile.vac_banned) {
        return "VAC Banned";
      } else if (profile.game_banned) {
        return "Game Banned";
      }
      return "No Bans";
    };

    return (
      <div style={{
        border: "1px solid #ddd",
        borderRadius: "8px",
        marginBottom: "10px",
        backgroundColor: "#fff",
        overflow: "hidden"
      }}>
        {/* Collapsed View */}
        <div 
          onClick={() => toggleProfileExpansion(profile.steam_id)}
          style={{
            padding: "15px",
            cursor: "pointer",
            backgroundColor: isExpanded ? "#f8f9fa" : "#fff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: isExpanded ? "1px solid #ddd" : "none"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
            <img 
              src={profile.avatar} 
              alt="Avatar" 
              style={{ width: "40px", height: "40px", borderRadius: "50%" }}
            />
            <div>
              <div style={{ fontWeight: "bold", fontSize: "16px" }}>
                {profile.nickname}
              </div>
              <div style={{ fontSize: "12px", color: "#666" }}>
                {profile.steam_id}
              </div>
            </div>
          </div>
          
          <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
            <span style={{
              padding: "4px 8px",
              borderRadius: "4px",
              fontSize: "12px",
              color: "white",
              backgroundColor: getStatusColor(profile.current_status)
            }}>
              {getBanInfo()}
            </span>
            
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: "14px", fontWeight: "bold" }}>
                {profile.tracked_by_count} users tracking
              </div>
              <div style={{ fontSize: "12px", color: "#666" }}>
                Since {new Date(profile.first_tracked_date).toLocaleDateString()}
              </div>
            </div>
            
            <span style={{ fontSize: "16px", color: "#666" }}>
              {isExpanded ? "▼" : "▶"}
            </span>
          </div>
        </div>

        {/* Expanded View */}
        {isExpanded && (
          <div style={{ padding: "20px", backgroundColor: "#f8f9fa" }}>
            {/* Basic Stats */}
            <div style={{ marginBottom: "20px" }}>
              <h4 style={{ marginBottom: "10px", color: "#333" }}>Basic Stats</h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                <div>
                  <p><strong>CS2 Playtime:</strong> {profile.cs2_playtime} hours</p>
                  <p><strong>VAC Banned:</strong> {profile.vac_banned ? "Yes" : "No"}</p>
                  <p><strong>Game Banned:</strong> {profile.game_banned ? "Yes" : "No"}</p>
                  <p><strong>Total Bans:</strong> {profile.total_bans}</p>
                </div>
              </div>
            </div>

            {/* Tags Statistics */}
            {profile.tag_stats && profile.tag_stats.length > 0 && (
              <div style={{ marginBottom: "20px" }}>
                <h4 style={{ marginBottom: "10px", color: "#333" }}>Tag Statistics</h4>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                  {profile.tag_stats.map(tag => (
                    <span key={tag.name} style={{
                      padding: "6px 12px",
                      backgroundColor: "#007bff",
                      color: "white",
                      borderRadius: "16px",
                      fontSize: "14px"
                    }}>
                      {tag.name} - x{tag.count}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Match History */}
            {profile.match_history && profile.match_history.length > 0 && (
              <div>
                <h4 style={{ marginBottom: "10px", color: "#333" }}>Recent Matches</h4>
                <div style={{ 
                  backgroundColor: "white", 
                  borderRadius: "4px",
                  border: "1px solid #ddd",
                  overflow: "hidden"
                }}>
                  {profile.match_history.map((match, index) => (
                    <div 
                      key={match.match_id}
                      style={{
                        padding: "10px 15px",
                        borderBottom: index < profile.match_history.length - 1 ? "1px solid #ddd" : "none",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: "bold" }}>{match.match_id}</div>
                        <div style={{ fontSize: "12px", color: "#666" }}>
                          {new Date(match.date_added).toLocaleDateString()}
                        </div>
                      </div>
                      {match.tags && match.tags.length > 0 && (
                        <div style={{ display: "flex", gap: "5px" }}>
                          {match.tags.map(tag => (
                            <span key={tag} style={{
                              padding: "2px 6px",
                              backgroundColor: "#e9ecef",
                              color: "#495057",
                              borderRadius: "8px",
                              fontSize: "12px"
                            }}>
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <p>Loading leaderboards...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "800px", margin: "30px auto", fontFamily: "Arial, sans-serif" }}>
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center",
        marginBottom: "20px"
      }}>
        <h2 style={{ margin: 0 }}>Global Leaderboards</h2>
        <div style={{ fontSize: "14px", color: "#666" }}>
          {stats.banned} banned accounts out of {stats.total} tracked ({Math.round((stats.banned / stats.total) * 100) || 0}%)
        </div>
      </div>

      {error && (
        <div style={{ 
          padding: "10px", 
          backgroundColor: "#f8d7da", 
          color: "#721c24", 
          borderRadius: "4px",
          marginBottom: "15px"
        }}>
          {error}
        </div>
      )}

      <div>
        {profiles.length === 0 ? (
          <div style={{ 
            padding: "20px", 
            backgroundColor: "#f5f5f5", 
            borderRadius: "8px",
            textAlign: "center",
            color: "#666"
          }}>
            No profiles are being tracked globally yet.
          </div>
        ) : (
          <div>
            {profiles
              .sort((a, b) => b.tracked_by_count - a.tracked_by_count)
              .map((profile) => (
                <ProfileCard key={profile.steam_id} profile={profile} />
              ))
            }
          </div>
        )}
      </div>
    </div>
  );
}

export default Leaderboards;